import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/auth";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";

import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Cases from "./pages/Cases";
import CaseDetail from "./pages/CaseDetail";
import Documents from "./pages/Documents";
import ParkingAssistant from "./pages/ParkingAssistant";
import Admin from "./pages/Admin";
import AuditLog from "./pages/AuditLog";
import AgencyDigest from "./pages/AgencyDigest";
import EstateMapAdmin from "./pages/EstateMapAdmin";
import AdminTriage from "./pages/AdminTriage";
import Privacy from "./pages/Privacy";
import AdminPrivacy from "./pages/AdminPrivacy";
import Notifications from "./pages/Notifications";
import SharedAppealReview from "./pages/SharedAppealReview";
import EstateCalendar from "./pages/EstateCalendar";
import SignUp from "./pages/SignUp";
import JoinEstate from "./pages/JoinEstate";
import Onboarding from "./pages/Onboarding";
import LocalityLibrary from "./pages/LocalityLibrary";
import Guidance from "./pages/Guidance";
import WhereMoneyGoes from "./pages/WhereMoneyGoes";
import PackScore from "./pages/PackScore";
import LifeInArea from "./pages/LifeInArea";
import RunEstate from "./pages/RunEstate";
import Handover from "./pages/Handover";
import PrivacyNotice from "./pages/PrivacyNotice";
import ResponderHome from "./pages/ResponderHome";
import Landing from "./pages/Landing";
import DatesRegister from "./pages/DatesRegister";
import { useAuth } from "./lib/auth";
import TermsOfService from "./pages/TermsOfService";
import NotFound from "./pages/NotFound";

const guarded = (children, roles) => (
    <ProtectedRoute roles={roles}>
        <Layout>
            <ErrorBoundary>{children}</ErrorBoundary>
        </Layout>
    </ProtectedRoute>
);

// "/" is the dashboard for a signed-in user and the public landing page for everyone else.
function RootRoute() {
    const { user, loading } = useAuth();
    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
    }
    return user ? guarded(<Dashboard />) : <Landing />;
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <Toaster position="top-right" richColors closeButton />
                    <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/shared/appeal/:token" element={<SharedAppealReview />} />
                        {/* Unauthenticated on purpose — a notice that only appears after signup is
                            too late to inform the decision to sign up (UK GDPR Arts.13-14). */}
                        <Route path="/privacy-notice" element={<PrivacyNotice />} />
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route path="/signup" element={<SignUp />} />
                        <Route path="/responder" element={<ResponderHome />} />
                        <Route path="/join" element={<JoinEstate />} />
                        <Route path="/join/:code" element={<JoinEstate />} />
                        <Route path="/onboarding" element={guarded(<Onboarding />, ["estate_admin"])} />
                        <Route path="/locality" element={guarded(<LocalityLibrary />)} />
                        <Route path="/guidance" element={guarded(<Guidance />)} />
                        <Route path="/life" element={guarded(<LifeInArea />)} />
                        <Route
                            path="/money"
                            element={guarded(<WhereMoneyGoes />, ["resident", "household_admin", "estate_admin"])}
                        />
                        <Route
                            path="/pack-score"
                            element={guarded(<PackScore />, ["resident", "household_admin", "estate_admin"])}
                        />
                        <Route
                            path="/run"
                            element={guarded(<RunEstate />, ["resident", "household_admin", "estate_admin"])}
                        />
                        <Route path="/dates" element={guarded(<DatesRegister />, ["estate_admin"])} />
                        <Route path="/handover" element={guarded(<Handover />, ["estate_admin"])} />
                        <Route path="/" element={<RootRoute />} />
                        <Route path="/cases" element={guarded(<Cases />)} />
                        <Route path="/calendar" element={guarded(<EstateCalendar />)} />
                        <Route path="/cases/:id" element={guarded(<CaseDetail />)} />
                        <Route path="/documents" element={guarded(<Documents />)} />
                        <Route path="/notifications" element={guarded(<Notifications />)} />
                        <Route path="/privacy" element={guarded(<Privacy />, ["resident", "household_admin", "estate_admin"])} />
                        <Route
                            path="/parking"
                            element={guarded(<ParkingAssistant />, ["resident", "household_admin", "estate_admin"])}
                        />
                        <Route path="/admin" element={guarded(<Admin />, ["estate_admin"])} />
                        <Route path="/admin/triage" element={guarded(<AdminTriage />, ["estate_admin"])} />
                        <Route path="/admin/privacy" element={guarded(<AdminPrivacy />, ["estate_admin"])} />
                        <Route path="/admin/map" element={guarded(<EstateMapAdmin />, ["estate_admin"])} />
                        <Route path="/audit" element={guarded(<AuditLog />, ["estate_admin"])} />
                        <Route
                            path="/agency"
                            element={guarded(<AgencyDigest />, ["estate_admin", "agency_viewer"])}
                        />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
